import { Tooltip } from "antd/es";
import _ from "lodash";
import { PureComponent, ReactNode } from "react";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import { CommonProps } from "../common-props";
import { DashboardState } from "../kuika-cl-model-runtimes";

declare let window: any;

export type IconLibrary = "material" | "fontawesome";
type MaterialIconType = "filled" | "outlined" | "round" | "sharp" | "two-tone";
type FontAwesomeIconType = "solid" | "regular" | "brands";
export type IconType = MaterialIconType | FontAwesomeIconType;

const MATERIAL_ICON_CLASS_MAP: Record<MaterialIconType, string> = {
  filled: "material-icons",
  outlined: "material-icons-outlined",
  round: "material-icons-round",
  sharp: "material-icons-sharp",
  "two-tone": "material-icons-two-tone"
};

const FA_PREFIX_MAP: Record<FontAwesomeIconType, string> = {
  solid: "fa-solid",
  regular: "fa-regular",
  brands: "fa-brands"
};

const MATERIAL_ICON_TYPES: readonly MaterialIconType[] = ["filled", "outlined", "round", "sharp", "two-tone"];
const FA_ICON_TYPES: readonly FontAwesomeIconType[] = ["solid", "regular", "brands"];

const isMaterialIconType = (value: string | undefined): value is MaterialIconType =>
  value !== undefined && (MATERIAL_ICON_TYPES as readonly string[]).includes(value);

const isFontAwesomeIconType = (value: string | undefined): value is FontAwesomeIconType =>
  value !== undefined && (FA_ICON_TYPES as readonly string[]).includes(value);

interface IconProps {
  iconName: string;
  style: any;
  hoverText?: string;
  rounded?: boolean;
  iconType?: IconType;
  iconLibrary?: IconLibrary;
}

interface IconState {}

class Icon extends PureComponent<IconProps & CommonProps, IconState> {
  constructor(props: IconProps) {
    super(props);
    this.state = {};
  }

  getProps = () => {
    let result: any = {};
    if (this.props && this.props.style) {
      result = _.clone(this.props);
      if (result && result.style) {
        delete result.style;
      }
      if (result && result.rounded) {
        delete result.rounded;
      }
      if (result && result.iconType) {
        delete result.iconType;
      }
      if (result && result.iconLibrary) {
        delete result.iconLibrary;
      }
    }
    return result;
  };

  getStyleProp = () => {
    let style: any = {};

    if (this.props.style) {
      style = _.clone(this.props.style);
      if (this.props.style?.fontFamily) {
        delete style.fontFamily;
      }
    }
    if (this.props.showCursorPointer) {
      style.cursor = "pointer";
    }
    if (style.display !== "block") {
      style.display = "inline-block";
    }
    style.verticalAlign = "middle";

    if (window?.kuika?.dashboardState === DashboardState.reportDesigner) {
      style.height = "100%";
      style.display = "flex";
      style.alignItems = "center";
      style.justifyContent = "center";
    }
    return style;
  };

  isFontAwesome = (): boolean => {
    return this.props.iconLibrary === "fontawesome";
  };

  getMaterialClassName = (): string => {
    if (isMaterialIconType(this.props.iconType)) {
      return MATERIAL_ICON_CLASS_MAP[this.props.iconType];
    }
    if (this.props.rounded) {
      return "material-icons-round";
    }
    return "material-icons";
  };

  getFontAwesomeClassName = (): string => {
    const faType: FontAwesomeIconType = isFontAwesomeIconType(this.props.iconType) ? this.props.iconType : "solid";
    const prefix = FA_PREFIX_MAP[faType];
    const name = this.props.iconName ? `fa-${this.props.iconName}` : "";
    return `${prefix} ${name}`.trim();
  };

  render(): ReactNode {
    const isFa: boolean = this.isFontAwesome();
    if (isFa && !this.props.iconName) {
      return null;
    }
    const iconClass: string = isFa ? `${this.getFontAwesomeClassName()} md-48` : `${this.getMaterialClassName()} md-48`;
    const innerContent: string | null = isFa ? null : this.props.iconName;

    // Merge any caller-supplied className (notably the generator's
    // `kuika-style-<id>`, which the state CSS targets via :hover/:active/
    // :focus-visible/[data-disabled]) with the icon-font class. Previously
    // `className={iconClass}` sat AFTER the props spread and silently overrode
    // the caller's class — so state CSS never matched the icon's <i> and e.g. a
    // hover background-color did nothing. Extract + delete the spread's
    // className, then set the combined value explicitly.
    const iconProps: any = this.getProps();
    const passedClassName: string = typeof iconProps.className === "string" ? iconProps.className : "";
    delete iconProps.className;
    const combinedClassName: string = `${iconClass} ${passedClassName}`.trim();

    if (this.props.hoverText) {
      return (
        <>
          <Tooltip color={"white"} title={this.props.hoverText}>
            <i style={this.getStyleProp()} {...iconProps} className={combinedClassName}>
              {innerContent}
            </i>
          </Tooltip>
        </>
      );
    }
    return (
      <>
        <i style={this.getStyleProp()} {...iconProps} className={combinedClassName}>
          {innerContent}
        </i>
      </>
    );
  }
}

const icon = withCommonEvents(Icon);
export { icon as Icon };
